// src/components/admin/EmailManagement.js
// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {useNavigate, Navigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../api/axios';
import {useAuth} from '../../hooks/useAuth';
import NewsletterEmail from '../../email/NewsletterEmail';
import AnnouncementEmail from '../../email/AnnouncementEmail';
import NewEmail from './../../email/NewEmail';

const Container = styled.div`
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
`;

const Title = styled.h3`
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  gap: 10px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
`;

const PreviewButton = styled(Button)`
  background-color: #28a745;
  &:hover {
    background-color: #218838;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background: #ccc;
  border-radius: 5px;
  margin-top: 40px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #007bff;
  width: ${props => props.progress}%;
  transition: width 0.3s ease-in-out;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  width: 100%;
`;

const emailTemplates = {
  newsletter: NewsletterEmail,
  announcement: AnnouncementEmail,
  newEmail: NewEmail,
};

const emailOptions = {
  newsletter: 'Send Newsletter',
  announcement: 'Send Announcement',
  newEmail: 'New Email',
};

const EmailManagement = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [emailOption, setEmailOption] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState(''); // Editor-Inhalt als HTML
  const [recipientCount, setRecipientCount] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Setze isMounted beim Mount und beim Unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (emailOption) {
      fetchRecipients(emailOption);
    }
  }, [emailOption]);

  if (!user || user.permission !== 3) {
    return <Navigate to="/" replace />;
  }

  const fetchRecipients = async type => {
    try {
      const response = await api.get(`/email_templates/get_recipients.php?type=${type}`);
      if (response.data.success && isMounted.current) {
        setRecipientCount(response.data.recipient_count || 0);
        setRecipients(response.data.recipients || []);
      }
    } catch (error) {
      if (isMounted.current) {
        throw error.response?.data?.error;
      }
    }
  };

  const handleSendEmail = async e => {
    e.preventDefault();
    try {
      let count = 0;
      const messageHTML = content; // ReactQuill liefert bereits HTML
      for (const recipient of recipients) {
        const recipientData = typeof recipient === 'string' ? {email: recipient, username: 'User'} : recipient;
        if (!recipientData.email) continue;

        const EmailComponent = emailTemplates[emailOption];
        const emailHTML = `<html>${EmailComponent({
          subject,
          username: recipientData.username,
          message: messageHTML,
          emailAddress: recipientData.email,
        })}</html>`;

        await api.post(
          'email_templates/send_mailer.php',
          {
            email: recipientData.email.trim(),
            subject,
            body: emailHTML,
            plainText: messageHTML.replace(/<[^>]+>/g, ''),
          },
          {headers: {'Content-Type': 'application/json'}}
        );
        count++;
        if (isMounted.current) {
          setProgress(Math.round((count / recipients.length) * 100));
        }
      }
      if (isMounted.current) {
        navigate(
          `?popup=success&msg=${encodeURIComponent(
            t('emailManagement.successMessage', {sent: count, total: recipients.length})
          )}`
        );
        setSubject('');
        setContent('');
        setRecipientCount(0);
        setProgress(0);
      }
    } catch (error) {
      if (isMounted.current) {
        navigate(`?popup=error&msg=${encodeURIComponent(t('emailManagement.errorSendingEmails'))}`);
      }
    }
  };

  const openPreview = () => {
    const EmailComponent = emailTemplates[emailOption];
    const previewHTML = `<html>${EmailComponent({
      subject,
      username: 'User',
      message: content,
      emailAddress: 'user@example.com',
    })}</html>`;
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    previewWindow.document.write(previewHTML);
    previewWindow.document.close();
  };

  return (
    <Container>
      <Title>{t('emailManagement.title')}</Title>
      <OptionsContainer>
        <Select value={emailOption} onChange={e => setEmailOption(e.target.value)}>
          <option value="" disabled>
            {t('emailManagement.selectEmailType')}
          </option>
          {Object.entries(emailOptions).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
        {emailOption && <p>{t('emailManagement.recipients', {count: recipientCount})}</p>}
      </OptionsContainer>
      {emailOption && (
        <Form onSubmit={handleSendEmail}>
          <Input
            type="text"
            placeholder={t('emailManagement.subjectPlaceholder')}
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
          />
          <ReactQuill
            value={content}
            onChange={newContent => {
              if (isMounted.current) setContent(newContent);
            }}
            theme="snow"
            style={{height: '300px'}}
          />
          <ProgressBar>
            <ProgressFill progress={progress} />
          </ProgressBar>
          <ButtonContainer>
            <PreviewButton type="button" onClick={openPreview}>
              {t('emailManagement.openPreview')}
            </PreviewButton>
            <Button type="submit">{t('emailManagement.sendEmails')}</Button>
          </ButtonContainer>
        </Form>
      )}
    </Container>
  );
};

export default EmailManagement;
